import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { USER, ALERT, MODAL, KPI, METADATA } from '@flexo/reducers';

const ENV = (import.meta as any).env;

interface UseMobileLogoutOptions {
  /**
   * If true, sets flag to redirect to forgot password page after logout
   * If false or undefined, redirects to login page
   */
  redirectToForgotPassword?: boolean;
  /**
   * Clerk signOut function from useAuth hook
   * Must be passed from component that uses Clerk hooks
   */
  clerkSignOut?: () => Promise<void>;
}

/**
 * Hook for handling mobile logout flow with proper state management
 * 
 * This hook provides a consistent logout function that:
 * - Shows splash screen with loading indicator
 * - Clears Redux stores (KPI, Metadata)
 * - Signs out from backend API
 * - Signs out from Firebase AND Clerk
 * - Triggers navigation via useLogoutRedirect hook
 * 
 * @param options - Configuration options for logout behavior
 * @returns logout function that can be called from components
 * 
 * @example
 * // Normal logout to login screen (with Clerk support)
 * const { signOut: clerkSignOut } = useAuth();
 * const handleLogout = useMobileLogout({ clerkSignOut });
 * 
 * @example
 * // Logout with redirect to forgot password
 * const handleLogout = useMobileLogout({ redirectToForgotPassword: true, clerkSignOut });
 */
export function useMobileLogout(options?: UseMobileLogoutOptions) {
  const dispatch = useDispatch();
  const { redirectToForgotPassword = false, clerkSignOut } = options || {};

  const logout = useCallback(async () => {
    console.log('🚪 [LOGOUT] Function called!');

    // Show splash screen FIRST to cover everything (including modal backdrops)
    dispatch(USER.ShowSplashScreen());
    dispatch(USER.SetIsLoggingOut());
    
    // Then close modal and alert underneath the splash screen
    dispatch(MODAL.CloseModal());
    dispatch(ALERT.ResetAlert());

    try {
      // Set redirect flag if needed (for forgot password flow)
      if (redirectToForgotPassword) {
        localStorage.setItem('redirectToForgotPassword', 'true');
      }

      // Clear app state first
      dispatch(KPI.ResetKpiApiStore());
      dispatch(METADATA.ResetMetadata());

      // CRITICAL FIX: Call Clerk signOut directly if available
      if (clerkSignOut) {
        console.log('[useMobileLogout] Calling Clerk signOut...');
        await clerkSignOut().catch((error) => {
          console.error('[useMobileLogout] Clerk signOut error:', error);
          // Continue with logout even if Clerk fails
        });
        console.log('[useMobileLogout] Clerk signOut complete');
      }

      // Signal logout via Redux - useLogoutRedirect hook will handle Firebase signout and navigation
      dispatch(USER.SetLogout());
    } catch (error) {
      console.error('[useMobileLogout] Logout error:', error);
      // Ensure logout happens even if there are errors
      dispatch(USER.SetLogout());
    } finally {
      // Reset any pending alerts to prevent session expired messages
      dispatch(ALERT.ResetAlert());
    }
  }, [redirectToForgotPassword, clerkSignOut, dispatch]);

  return logout;
}

